import { track } from "~/modules/analytics/track";
import { VisualizationUserEvents } from "~/modules/analytics/events/visualization";
import { AggregateFunctions, AggregateQueryInterval } from "../filters/types";
import { TokenTimeseriesGroupBy } from "../token_timeseries/types";

// // // //

/**
 * trackShowCumulativeChart
 * Tracks the event when a user successfully downloads an Excel export.
 */
export function trackShowCumulativeChart(props: { visualization: string }) {
    track(VisualizationUserEvents.ShowCumulativeChart, props);
}

/**
 * trackShowPercentOfTotalChart
 * Tracks the event when a user successfully downloads an Excel export.
 */
export function trackShowPercentOfTotalChart(props: { visualization: string }) {
    track(VisualizationUserEvents.ShowPercentOfTotalChart, props);
}

/**
 * trackShowChartLegend
 * Tracks the event when a user shows the chart legend
 */
export function trackShowChartLegend(props: { visualization: string }) {
    track(VisualizationUserEvents.ShowChartLegend, props);
}

/**
 * trackHideChartLegend
 * Tracks the event when a user hides the chart legend
 */
export function trackHideChartLegend(props: { visualization: string }) {
    track(VisualizationUserEvents.HideChartLegend, props);
}

/**
 * trackShowChartTimeframeNavigator
 * Tracks the event when a user shows the timeframe navigator
 */
export function trackShowChartTimeframeNavigator(props: {
    visualization: string;
}) {
    track(VisualizationUserEvents.ShowChartTimeframeNavigator, props);
}

/**
 * trackHideChartTimeframeNavigator
 * Tracks the event when a user hides the timeframe navigator
 */
export function trackHideChartTimeframeNavigator(props: {
    visualization: string;
}) {
    track(VisualizationUserEvents.HideChartTimeframeNavigator, props);
}

/**
 * trackChangeTimeseriesGroupBy
 * Tracks the event when a user changes the `group by` dropdown for the timeseries chart
 */
export function trackChangeTimeseriesGroupBy(props: {
    visualization: string;
    prevValue: string | TokenTimeseriesGroupBy;
    nextValue: string | TokenTimeseriesGroupBy;
}) {
    track(VisualizationUserEvents.ChangeTimeSeriesGroupBy, props);
}

/**
 * trackChangeTimeseriesAggregateFunction
 * Tracks the event when a user changes the `aggregate function` dropdown for the timeseries chart
 */
export function trackChangeTimeseriesAggregateFunction(props: {
    visualization: string;
    prevValue: string | AggregateFunctions;
    nextValue: string | AggregateFunctions;
}) {
    track(VisualizationUserEvents.ChangeTimeSeriesAggregateFunction, props);
}

/**
 * trackChangeTimeseriesInterval
 * Tracks the event when a user changes the `interval` dropdown for the timeseries chart
 */
export function trackChangeTimeseriesInterval(props: {
    visualization: string;
    prevValue: string | AggregateQueryInterval;
    nextValue: string | AggregateQueryInterval;
}) {
    track(VisualizationUserEvents.ChangeTimeSeriesInterval, props);
}

/**
 * trackChangeTimeseriesSegment
 * Tracks the event when a user changes a segment select control (e.g. the
 * "Underlying Type" ETF/ETP vs. Equity/Stock select) for the timeseries chart
 */
export function trackChangeTimeseriesSegment(props: {
    visualization: string;
    segmentLabel: string;
    prevValue: string;
    nextValue: string;
}) {
    track(VisualizationUserEvents.ChangeTimeSeriesSegment, props);
}

/**
 * trackChangeTimeseriesMode
 * Tracks the event when a user changes which "mode" or "tab" displayed in a visualization
 */
export function trackChangeTimeseriesMode(props: {
    prevValue: string;
    nextValue: string;
}) {
    track(VisualizationUserEvents.ChangeVisualizationMode, props);
}

/**
 * trackTimeSeriesLoginCtaOpen
 * Tracks the event when a user opens the LoginRequiredModal in the Time Series Visualization controls
 */
export function trackTimeSeriesLoginCtaOpen(visualizationModeKey: string) {
    track(VisualizationUserEvents.TimeSeriesLoginCtaOpen, {
        visualization: visualizationModeKey,
    });
}

/**
 * trackTimeSeriesLoginCtaClose
 * Tracks the event when a user closes the LoginRequiredModal in the Time Series Visualization controls
 */
export function trackTimeSeriesLoginCtaClose() {
    track(VisualizationUserEvents.TimeSeriesLoginCtaClose);
}

/**
 * trackTimeSeriesLoginCtaConfirmed
 * Tracks the event when a user clicks the login button in the LoginRequiredModal in the Time Series Visualization controls
 */
export function trackTimeSeriesLoginCtaConfirmed() {
    track(VisualizationUserEvents.TimeSeriesLoginCtaConfirmed);
}

/**
 * trackChangeMetricTimeseriesMetric
 * Tracks the event when a user changes the metric in MetricTimeseriesChart
 */
export function trackChangeMetricTimeseriesMetric(props: {
    chartName: string;
    prevValue: string;
    nextValue: string;
}) {
    track(VisualizationUserEvents.ChangeMetricTimeseriesMetric, props);
}
